/**
 * AuthProvider — wraps the app and manages Amplify v6 Cognito auth lifecycle.
 *
 * Responsibilities:
 * - Configures AWS Amplify v6 for Cognito
 * - Listens to Hub auth events (signedIn, signedOut, tokenRefresh)
 * - Extracts claims from Cognito tokens and populates the auth store
 * - Clears auth store on sign-out or token expiry
 */

import { portalRoleFromCognito } from '@smartsafe/shared';
import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useEffect, useState, type ReactNode } from 'react';

import type { AuthPayload } from '../types/auth-state';
import type { PortalUserRole } from '../types/portal-role';
import type { WhitelabelConfig } from '../types/whitelabel';
import { useAuthStore } from '../stores/auth-store';

/**
 * Amplify configuration — values injected at build time via Vite env vars.
 */
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
      ...(import.meta.env.VITE_COGNITO_DOMAIN
        ? {
            loginWith: {
              oauth: {
                domain: import.meta.env.VITE_COGNITO_DOMAIN,
                scopes: ['openid', 'profile', 'email'],
                redirectSignIn: [import.meta.env.VITE_REDIRECT_SIGN_IN ?? window.location.origin],
                redirectSignOut: [import.meta.env.VITE_REDIRECT_SIGN_OUT ?? window.location.origin],
                responseType: 'code' as const,
              },
            },
          }
        : {}),
    },
  },
};

Amplify.configure(amplifyConfig);

async function notifySessionAudit(event: 'login' | 'logout'): Promise<void> {
  try {
    await fetch('/bff/auth/audit-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
    });
  } catch {
    // Fire-and-forget — audit must not block auth UX.
  }
}

/**
 * Maps a Cognito role claim (e.g. 'PLATFORM_ADMIN', 'DESIGNADOR_ADMIN') to the
 * portal's lowercase PortalUserRole via the single catalog (ADR-053). The SPA
 * keeps its historical fallback to 'client_viewer' (the BFF answers 401).
 */
export function mapCognitoRole(cognitoRole: string | undefined): PortalUserRole {
  return portalRoleFromCognito(cognitoRole) ?? 'client_viewer';
}

/**
 * Extracts auth payload from Cognito JWT claims.
 */
async function extractAuthPayload(): Promise<AuthPayload | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;

    if (!idToken) {
      return null;
    }

    const claims = idToken.payload;

    const userId = claims['sub'] ?? '';
    const tenantId = (claims['custom:tenantId'] as string | undefined) ?? '';
    const role = mapCognitoRole(claims['custom:role'] as string | undefined);

    // Extract display name from standard Cognito claims
    const givenName = (claims['given_name'] as string | undefined)?.trim();
    const familyName = (claims['family_name'] as string | undefined)?.trim();
    const rawDisplayName =
      (claims['name'] as string | undefined)?.trim() ||
      [givenName, familyName].filter(Boolean).join(' ') ||
      undefined;

    // Fallback: use email prefix capitalized (e.g. "admin@smartsafe.dev" → "Admin")
    let displayName = rawDisplayName;
    if (!displayName) {
      const email = (claims['email'] as string | undefined) ?? '';
      if (email) {
        const prefix = email.split('@')[0] ?? '';
        displayName = prefix.charAt(0).toUpperCase() + prefix.slice(1);
      }
    }

    let featureFlags: Record<string, boolean> = {};
    const flagsClaim = claims['custom:featureFlags'] as string | undefined;
    if (flagsClaim) {
      try {
        featureFlags = JSON.parse(flagsClaim) as Record<string, boolean>;
      } catch {
        featureFlags = {};
      }
    }

    let whitelabelConfig: WhitelabelConfig | null = null;
    const wlClaim = claims['custom:whitelabelConfig'] as string | undefined;
    if (wlClaim) {
      try {
        whitelabelConfig = JSON.parse(wlClaim) as WhitelabelConfig;
      } catch {
        whitelabelConfig = null;
      }
    }

    return { userId, displayName, tenantId, role, featureFlags, whitelabelConfig };
  } catch {
    return null;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider component.
 *
 * Wraps the application and handles:
 * - Initial session check on mount
 * - Hub event listeners for auth state changes
 * - Populating/clearing the Zustand auth store
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      try {
        await getCurrentUser();
        const payload = await extractAuthPayload();
        if (!cancelled && payload) {
          setAuth(payload);
        }
      } catch {
        if (!cancelled) {
          clearAuth();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void checkSession();

    const hubListener = Hub.listen('auth', ({ payload: event }) => {
      void (async () => {
        switch (event.event) {
          case 'signedIn': {
            const authPayload = await extractAuthPayload();
            if (authPayload) {
              setAuth(authPayload);
              void notifySessionAudit('login');
            }
            break;
          }
          case 'tokenRefresh': {
            const authPayload = await extractAuthPayload();
            if (authPayload) {
              setAuth(authPayload);
            }
            break;
          }
          case 'signedOut':
          case 'tokenRefresh_failure': {
            clearAuth();
            break;
          }
        }
      })();
    });

    return () => {
      cancelled = true;
      hubListener();
    };
  }, [setAuth, clearAuth]);

  if (isLoading) {
    return null;
  }

  return <>{children}</>;
}

/**
 * Sign out the current user via Amplify and redirect to the login screen.
 */
export async function signOut(): Promise<void> {
  try {
    await notifySessionAudit('logout');
  } catch {
    // Continue sign-out even if audit fails.
  }
  useAuthStore.getState().clearAuth();
  try {
    await amplifySignOut({ global: true });
  } catch {
    // Local session may already be cleared — still redirect to login.
  }
  window.location.assign('/login');
}
